import React, { useEffect, useRef, useState } from 'react'
import { Link, useHistory, useLocation, useRouteMatch } from 'react-router-dom'
import { Drawer } from '../../../../shared/drawer/drawer'
import { useAuditCapabilities } from '../../../../shared/audit/use-audit-capabilities'
import { addressRequestsHref, aroundHref, requestChangesHref, sessionChangesHref, sessionRequestsHref, traceRequestsHref } from '../../../../shared/audit/trace-links'
import { Pill } from '../../../../shared/sso-workspace/pill'
import { formatCount, serverMessage } from '../../../../shared/sso-workspace/format'
import { viaText } from '../../../../shared/audit/entry-format'
import { isCode } from '../../../../shared/error/server-message'
import { getRequest } from './request-log-api'
import { formatMs, msClass, routeLabel, statusTone } from './format'
import { LocalTime } from '../../../../shared/date/local-time-view'
import { originRows } from './origin'
import { useCopy } from '../../../../shared/clipboard/clipboard'
import { ErrorReference } from '../../../../shared/error/error-reference'
import { explain } from './explanations'
import { traceLabel } from './trace-kind'
import { windowText } from './request-log-view'
import { OrganizationRef } from '../../../../shared/org-search/organization-ref'
import './request-log.scss'

const NOT_FOUND = "This request isn't in the log (it may be older than the retention period)."

// One request from the log, in the shared Drawer (a bottom sheet on phones).
// requestID is always the log's own id (minted by the request log); an
// X-Request-Id the client sent is only one of its request headers.
//
// requestID may be only the start of one (8 or more characters: what an
// error's reference or the grid's column shows is pasted here). Content
// answers the one request it starts, and onResolved hands its full id on;
// characters that start more than one are a 409 (request_log.ambiguous),
// answered with every request they start.
export const RequestDetail = ({ requestID, at, onClose, back, onResolved }) => {
  const [state, setState] = useState({ loading: true })
  const shown = useRef(null)
  const resolved = useRef(onResolved)
  resolved.current = onResolved

  useEffect(() => {
    if (!requestID) return undefined
    // The URL now names the request already shown (its full id).
    if (shown.current && shown.current.requestID === requestID) return undefined
    let live = true
    setState({ loading: true })
    getRequest(requestID, at).then(
      d => {
        if (!live) return
        shown.current = d || null
        setState(d ? { detail: d } : { notFound: true })
        if (d && d.requestID && d.requestID !== requestID && resolved.current) resolved.current(d.requestID)
      },
      err => {
        if (!live) return
        shown.current = null
        setState(err && err.status === 404
          ? { notFound: true }
          : { error: serverMessage(err, "This request couldn't be loaded."), failure: err, ambiguous: isCode(err, 'request_log.ambiguous') })
      }
    )
    return () => { live = false }
  }, [requestID, at])

  const d = state.detail
  const title = d ? `${d.method} ${routeLabel(d)}` : 'Request'

  return (
    <Drawer open={Boolean(requestID)} size="lg" title={title} onClose={onClose} back={back}>
      {state.loading && <p className="sw-hint m-0">Loading…</p>}
      {state.notFound && <p className="m-0">{NOT_FOUND}</p>}
      {state.error && <p className="text-danger small m-0" role="alert">{state.error}</p>}
      {state.error && <ErrorReference error={state.failure} className="mt-1" />}
      {state.ambiguous &&
        <p className="small mt-2 mb-0">
          <Link to={{ pathname: '/admin/request-log', search: `?request=${encodeURIComponent(requestID)}` }}>
            {`Show every request whose ID starts with ${requestID}`}
          </Link>
        </p>}
      {d && <Detail d={d} />}
    </Drawer>
  )
}

// The drawer for the route /admin/request-log/:requestID. Closing it goes
// back to the list with the list's filters (its query string); opened from
// somewhere else (route state { from: { label, to } }) it offers the way back.
// ?at= (when the request ran, from an error or an audit entry) is passed to
// the lookup and isn't one of the list's filters, so it's left behind.
export const RequestDetailRoute = () => {
  const match = useRouteMatch('/admin/request-log/:requestID')
  const history = useHistory()
  const location = useLocation()
  const from = location.state && location.state.from
  const requestID = match ? match.params.requestID : undefined
  const params = new URLSearchParams(location.search)
  const at = params.get('at') || undefined
  params.delete('at')
  const listSearch = params.toString() ? `?${params.toString()}` : ''

  return (
    <RequestDetail
      requestID={requestID}
      at={at}
      onClose={() => history.push({ pathname: '/admin/request-log', search: listSearch })}
      back={from ? { label: from.label, onClick: () => history.push(from.to) } : undefined}
      onResolved={full => history.replace({ pathname: `/admin/request-log/${full}`, search: location.search, state: location.state })}
    />
  )
}

const Section = ({ id, title, children }) =>
  <section className="rl-section" aria-labelledby={`rl-${id}`}>
    <h3 className="rl-section-title" id={`rl-${id}`}>{title}</h3>
    {children}
  </section>

// Key/value pairs (not tabular data): the workspace's definition list.
// A row whose value is empty is left out.
const KV = ({ rows }) =>
  <dl className="sw-dl">
    {rows.filter(([, v]) => v !== null && v !== undefined && v !== '' && v !== false).map(([k, v]) =>
      <React.Fragment key={k}><dt>{k}</dt><dd>{v}</dd></React.Fragment>)}
  </dl>

const geoOf = d => {
  if (!d.geo) return null
  if (typeof d.geo === 'string') {
    try { return JSON.parse(d.geo) } catch (e) { return null }
  }
  return d.geo
}

// A value with the links that follow it, under it (each reads as a
// sentence; a link the reader can't use is left out).
const Traced = ({ value, links, note }) => {
  const shown = links.filter(Boolean)
  if (!shown.length) return value
  return (
    <span className="rl-traced">
      {value}
      <span className="rl-trace-links">
        {shown}
        {note && <span className="sw-hint">{note}</span>}
      </span>
    </span>
  )
}

const Detail = ({ d }) => {
  // the audit log's links only for a reader who has it
  const { sysAuditLog } = useAuditCapabilities()
  const around = aroundHref(d.occurredAt)
  const geo = geoOf(d) || {}
  const place = [geo.city, geo.region, geo.country || d.country].filter(Boolean).join(', ')
  const network = [geo.asn ? `AS${geo.asn}` : '', geo.network].filter(Boolean).join(' ')
  const headers = d.requestHeaders && typeof d.requestHeaders === 'object' ? Object.entries(d.requestHeaders) : []
  const meaning = explain(d)
  const address = addressRequestsHref(d.ip, d.occurredAt)

  return (
    <div className="rl-detail">
      {d.redactionFailed &&
        <div className="sw-notice warn" role="status"><div>Removing secrets from this request failed, so its bodies were not stored.</div></div>}

      <Section id="summary" title="Summary">
        <KV rows={[
          ['Time', <Traced
            value={<LocalTime value={d.occurredAt} seconds />}
            links={[around && <Link key="around" to={around}>Every request around this moment</Link>]}
            note="10 seconds either side, every service and person" />],
          ['Status', <Pill tone={statusTone(d.status)}>{String(d.status)}</Pill>],
          ['Duration', <span className={msClass(d.durationMs)}>{formatMs(d.durationMs)}</span>],
          ['Service', d.service],
          ['Request ID', <Traced
            value={<CopyText value={d.requestID} label="Copy the request ID" />}
            links={[sysAuditLog && <Link key="changes" to={requestChangesHref(d.requestID, d.occurredAt)}>Changes this request made</Link>]} />],
          ['Session', d.sessionID && <Traced
            value={<span className="sw-mono rl-id">{d.sessionID}</span>}
            links={[
              <Link key="requests" to={sessionRequestsHref(d.sessionID, d.occurredAt)}>Every request in this session</Link>,
              sysAuditLog && <Link key="changes" to={sessionChangesHref(d.sessionID)}>Changes in this session</Link>
            ]} />]
        ]} />
      </Section>

      {d.traceKey && <Attempt d={d} />}

      {meaning &&
        <section className={`sw-notice rl-meaning${meaning.tone && meaning.tone !== 'info' ? ` ${meaning.tone}` : ''}`} aria-labelledby="rl-meaning" data-explanation={meaning.id}>
          <div>
            <h3 className="rl-meaning-title" id="rl-meaning">What this means: {meaning.title}</h3>
            {meaning.lines.map(line => <p key={line}>{line}</p>)}
          </div>
        </section>}

      {d.error &&
        <Section id="error" title="Error">
          <p className="small sw-hint">Shown with secrets removed.</p>
          <pre className="rl-error sw-mono">{d.error}</pre>
        </Section>}

      <Section id="who" title="Who">
        {d.actorUserID || d.actorEmail || d.actorName
          ? <KV rows={[
              ['Name', d.actorName],
              ['Email', d.actorEmail],
              ['User ID', d.actorUserID],
              ['Login ID', d.actorLoginID],
              ['Organization', d.orgID ? <OrganizationRef id={d.orgID} /> : null],
              ['Impersonator', d.impersonatorLoginID && `Impersonated (login ${d.impersonatorLoginID})`],
              // an action in a learner's bounded LMS session names the connection it acted through
              // (content's viaConnection {id, name}; Phase F F-11, final review M7)
              ['Via', viaText(d)]
            ]} />
          : <p className="sw-hint m-0">Anonymous: no one was signed in.</p>}
      </Section>

      <Section id="app" title="App and location">
        <KV rows={[
          ['App', d.clientApp],
          ['Version', d.clientVersion],
          ['Platform', d.clientPlatform],
          // what content inferred when the log was read, or the flag for an
          // app nothing names; the three rows above stay as recorded
          ...originRows(d),
          ['IP', d.ip && <Traced
            value={<span className="sw-mono">{d.ip}</span>}
            links={[address && <Link key="address" to={address}>Every request from this address</Link>]}
            note="a day either side, every service and person" />],
          ['Location', place],
          ['Network', network && <span>{network} {geo.hosting && <Pill tone="info">Hosting provider</Pill>}</span>]
        ]} />
      </Section>

      <Section id="request" title="Request">
        <KV rows={[
          ['Path', <span className="sw-mono">{d.path}</span>],
          ['Query', d.query && <span className="sw-mono">{d.query}</span>],
          ['Size', `${formatCount(d.requestBytes)} bytes in, ${formatCount(d.responseBytes)} bytes out`]
        ]} />
        <h4 className="rl-sub">Headers</h4>
        {headers.length
          ? <dl className="sw-dl rl-headers">
              {headers.map(([k, v]) => <React.Fragment key={k}><dt className="sw-mono">{k}</dt><dd className="sw-mono">{Array.isArray(v) ? v.join(', ') : String(v)}</dd></React.Fragment>)}
            </dl>
          : <p className="sw-hint m-0">No headers were stored.</p>}
      </Section>

      <Section id="request-body" title="Request body">
        <Body body={d.requestBody} note={d.requestBodyNote} truncated={d.requestBodyTruncated} />
      </Section>

      <Section id="response-body" title="Response body">
        <Body body={d.responseBody} note={d.responseBodyNote} truncated={d.responseBodyTruncated} />
      </Section>
    </div>
  )
}

// The flow the request was part of (core request.NoteTrace: one SSO sign-in
// attempt's steps share a key, a one-way hash of its sign-in token):
// content's summary of it, a day either side of this request, and who it
// signed in. Its steps are anonymous (a sign-in has no session yet), so this
// is how a failed step finds the sign-in it belonged to.
const Attempt = ({ d }) => {
  const t = d.trace
  const label = traceLabel(d.traceKey)
  const all = traceRequestsHref(d.traceKey, d.occurredAt)
  const signed = t && t.signedIn
  const who = signed && ([signed.name, signed.email].filter(Boolean).join(' · ') || (signed.loginID ? `login ${signed.loginID}` : 'someone'))
  const count = t ? `${formatCount(t.requests)} request${t.requests === 1 ? '' : 's'}${t.failed ? `, ${formatCount(t.failed)} answered with an error status` : ''}` : null
  return (
    <Section id="attempt" title={`Part of a ${label}`}>
      <KV rows={[
        ['Requests', <Traced
          value={t
            ? <span>{count}{t.first && t.last ? <span className="sw-hint">{` · ${windowText(t.first, t.last)}`}</span> : null}</span>
            : <span className="sw-hint">{`Every request in this ${label} carries the same key.`}</span>}
          links={[all && <Link key="all" to={all}>{`Every request in this ${label}`}</Link>]}
          note="a day either side" />],
        ['Signed in as', label === 'sign-in attempt' && (signed
          ? <Traced
              value={<span>{who}{signed.at ? <> <span className="sw-hint">at</span> <LocalTime value={signed.at} seconds /></> : null}</span>}
              links={[signed.requestID && <Link key="signin" to={{ pathname: `/admin/request-log/${signed.requestID}`, search: signed.at ? `?at=${encodeURIComponent(signed.at)}` : '' }}>The request that signed in</Link>]} />
          : t ? <span className="sw-hint">No request in this attempt signed anyone in.</span> : null)]
      ]} />
    </Section>
  )
}

// A stored body, pretty-printed when it is JSON; else the server's note on
// why it was not stored.
const pretty = body => {
  try { return JSON.stringify(JSON.parse(body), null, 2) } catch (e) { return body }
}

const Body = ({ body, note, truncated }) => {
  if (body === null || body === undefined || body === '') {
    return <p className="sw-hint m-0">{note || 'No body.'}</p>
  }
  return (
    <>
      {truncated && <p className="sw-hint mb-1">Showing the first 64 KB.</p>}
      <pre className="rl-body">{truncated ? body : pretty(body)}</pre>
      {note && <p className="sw-hint m-0">{note}</p>}
    </>
  )
}

// Copy through the shared helper (shared/clipboard): Copied only after a
// real copy; otherwise the id is selected and the shortcut named.
const CopyText = ({ value, label }) => {
  const copy = useCopy()
  const text = useRef(null)
  return (
    <span className="rl-copy">
      <span className="sw-mono" ref={text}>{value}</span>
      <button type="button" className="btn btn-sm sw-btn" aria-label={label} onClick={() => copy.copy(value, text)}>{copy.copied ? 'Copied' : 'Copy'}</button>
      {copy.manual && <span className="sw-hint" role="status">{copy.hint}</span>}
    </span>
  )
}
